import { useI18n } from 'vue-i18n'
import { formatPriceDate, formatPriceTime, isPriceDateToday } from './format'
import { isPriceStale, type PriceFreshness, type PriceFreshnessInfo } from './model'

/**
 * All the stale-price wording in one place (`price_freshness.*`). Notes and
 * tags start with a lowercase letter, as everywhere in the product.
 */
export function usePriceFreshnessCopy() {
  const { t, locale } = useI18n()

  const date = (iso: string | null) => formatPriceDate(iso, locale.value)

  /** Tooltip on the clock icon and on the "outdated" store tag. */
  function tooltip(info: PriceFreshnessInfo): string {
    if (!isPriceStale(info.state)) return ''
    const when = date(info.updatedAt)
    if (info.state === 'unconfirmed') {
      return when
        ? t('price_freshness.tooltip_unconfirmed', { date: when })
        : t('price_freshness.tooltip_unconfirmed_no_date')
    }
    return when ? t('price_freshness.tooltip_outdated', { date: when }) : t('price_freshness.tooltip_outdated_no_date')
  }

  /** PDP note under the header price, fresh included ("updated today / Oct 18"). */
  function pdpNote(info: PriceFreshnessInfo): string {
    const when = date(info.updatedAt)
    if (info.state === 'unconfirmed') {
      return when ? t('price_freshness.note_unconfirmed', { date: when }) : t('price_freshness.note_unconfirmed_no_date')
    }
    if (info.state === 'outdated') {
      return when ? t('price_freshness.note_outdated', { date: when }) : t('price_freshness.note_outdated_no_date')
    }
    if (!info.updatedAt || !when) return ''
    return isPriceDateToday(info.updatedAt)
      ? t('price_freshness.note_fresh_today')
      : t('price_freshness.note_fresh_on', { date: when })
  }

  /**
   * Price history drawer, bottom note when every source is stale ("last updated Oct 18 at 13:30 —
   * the price may have changed" / "not updated since Oct 5 — check the price on the store’s site").
   */
  function historyNote(info: PriceFreshnessInfo): string {
    if (!isPriceStale(info.state)) return ''
    const when = date(info.updatedAt)
    if (info.state === 'unconfirmed') {
      return when
        ? t('price_freshness.history_note_unconfirmed', { date: when })
        : t('price_freshness.history_note_unconfirmed_no_date')
    }
    if (!when) return t('price_freshness.history_note_outdated_no_date')
    const time = formatPriceTime(info.updatedAt, locale.value)
    return time
      ? t('price_freshness.history_note_outdated', { date: when, time })
      : t('price_freshness.history_note_outdated_no_time', { date: when })
  }

  /** One line under the size pickers when availability is unknown. */
  function sizesNote(info: PriceFreshnessInfo): string {
    const when = date(info.updatedAt)
    return when ? t('price_freshness.sizes_note', { date: when }) : t('price_freshness.sizes_note_no_date')
  }

  /**
   * Everything a product card needs, for `v-bind`. A card without the field
   * gets `fresh` and stays as today.
   */
  function cardProps(model: object | null | undefined) {
    const item = model as { priceFreshness?: PriceFreshness; priceUpdatedAt?: string | null } | null | undefined
    const info: PriceFreshnessInfo = {
      state: item?.priceFreshness ?? 'fresh',
      updatedAt: item?.priceUpdatedAt ?? null,
    }
    return {
      priceState: info.state,
      priceStateTooltip: tooltip(info),
      unconfirmedLabel: t('price_freshness.not_confirmed'),
    }
  }

  return {
    tooltip,
    pdpNote,
    sizesNote,
    historyNote,
    cardProps,
    notConfirmed: () => t('price_freshness.not_confirmed'),
    outdatedTag: () => t('price_freshness.outdated_tag'),
    notifyDisabled: () => t('price_freshness.notify_disabled'),
  }
}
